import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { formatDate, formatDateTime } from '../../../lib/format';
import type { AuditRow, CursorPage, PayeeRateCardRow, PhysicianRow } from '@dru/types';
import {
  Alert, AttentionPanel, Button, DataTable, DetailHeader, DetailSection, ErrorState, Facts, InfoTip, KeyFigures,
  StatusBadge, Tabs, TextField, useToast, type CheckItem,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { holds } from '../../../layouts/ConsoleShell';
import { Dialog } from '../../../components/Dialog';
import { PayeeRates, currentRateCard } from '../components/PayeeRates';
import { PayeeSettlements } from '../components/PayeeSettlements';
import { useBreadcrumbTitle } from '../../../layouts/BreadcrumbContext';
import { PhysicianEditDialog, PhysicianStatusDialog, signingOf } from './Physicians';
import { useSignInAs } from '../components/ImpersonateDialog';
import { BRAND } from '@dru/types';

type Physician = PhysicianRow & {
  status: string;
  linked?: boolean;
  createdAt?: string;
};

/**
 * One certifying surgeon: who they are, whether they may sign today, and
 * what they are paid for signing.
 *
 * It opens on the question the panel exists to answer — can this person sign
 * a fitness certificate? — with every reason they cannot, because the three
 * facts behind it (active, AFIH, a linked login) were three unrelated badges
 * and a reader had to do the AND themselves.
 *
 * A physician belongs to the Dr Ultimate panel, not to a hospital or a
 * company: reviews reach them from any company and any venue, so there is no
 * "serves" list to show, and the page says so rather than leaving a gap.
 *
 * Both payees — venues and physicians — are paid against the same settlement
 * ledger, so both get the same rate card and statements over the same
 * components. Tabs are in the URL, matching `PartnerDetail`.
 */

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'fees', label: 'Fee agreement' },
  { key: 'statements', label: 'Statements' },
  { key: 'activity', label: 'Activity' },
];

export function PhysicianDetail() {
  const { id = '' } = useParams();
  const { permissions } = useAuth();
  const signIn = useSignInAs();
  const can = {
    edit: holds(permissions, 'user.update'),
    impersonate: holds(permissions, 'platform.impersonate'),
    invite: holds(permissions, 'user.create'),
    money: holds(permissions, 'settlement.read'),
    audit: holds(permissions, 'audit.read'),
  };
  const tabs = TABS.filter((t) => (t.key === 'fees' || t.key === 'statements' ? can.money
    : t.key === 'activity' ? can.audit : true));
  const [params, setParams] = useSearchParams();
  const wanted = params.get('tab') ?? 'overview';
  const tab = tabs.some((t) => t.key === wanted) ? wanted : 'overview';

  const physician = useQuery<Physician>(() => api.get(`/physicians/${id}`), [id]);
  const rates = useQuery<{ items: PayeeRateCardRow[] } | null>(
    () => (can.money ? api.get(`/settlements/rates/PHYSICIAN/${id}`) : Promise.resolve(null)), [id, can.money],
  );
  const [editing, setEditing] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [statusIntent, setStatusIntent] = useState<null | 'SUSPENDED' | 'ACTIVE'>(null);
  const toast = useToast();

  const p = physician.data;
  useBreadcrumbTitle(p?.fullName);

  const setTab = (key: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', key);
    setParams(next, { replace: true });
  };


  if (physician.error) {
    return (
      <div className="space-y-4">
        <ErrorState title="This physician could not be opened" detail={physician.error} onRetry={physician.reload} />
      </div>
    );
  }

  const signing = p ? signingOf(p) : null;
  const fee = rates.data ? currentRateCard(rates.data.items) : undefined;

  const checks: CheckItem[] = p ? [
    p.status === 'ACTIVE'
      ? { key: 'status', state: 'done', label: 'Active on the panel' }
      : { key: 'status', state: 'blocked', label: 'Suspended', detail: 'Cannot sign until reinstated.' },
    p.afihCertified
      ? { key: 'afih', state: 'done', label: 'AFIH certified', detail: p.afihNumber ? <span className="font-mono">{p.afihNumber}</span> : undefined }
      : { key: 'afih', state: 'blocked', label: 'Not AFIH certified', detail: 'Certification is refused without it, whatever the account may do.' },
    p.linked === false
      ? {
          key: 'login', state: 'blocked', label: 'No console login', detail: 'Can be assigned an examination and can never sign it.',
          action: can.invite ? <InlineAction onClick={() => setInviting(true)}>Invite to sign in</InlineAction> : undefined,
        }
      : p.loginStatus === 'INVITED'
        ? {
            key: 'login', state: 'todo', label: 'Invitation not accepted yet',
            detail: 'Cases are not given to them until they set a password and sign in.',
            action: can.invite ? <InlineAction onClick={() => setInviting(true)}>Resend invitation</InlineAction> : undefined,
          }
        : p.loginCompanyBound
          ? {
              key: 'login', state: 'warning', label: 'Login bound to one company',
              detail: 'Reviews only that company’s cases; other companies’ cases are not given to them. Ask support to move it to a panel login.',
            }
          : { key: 'login', state: 'done', label: 'Console login linked', detail: 'The person signing is the person named. Reviews cases from every company.' },
    p.isAvailable === false
      ? { key: 'away', state: 'warning', label: 'Marked away', detail: 'New reviews should not be routed here until they are back.' }
      : { key: 'away', state: 'done', label: 'Available for reviews' },
    fee === undefined
      ? null
      : fee
        ? { key: 'fee', state: 'done', label: 'Fee agreement in force', detail: fee.name }
        : {
            key: 'fee', state: 'todo', label: 'No fee agreement in force', detail: 'Signed reviews settle as unpriced until one is published.',
            action: (
              <button type="button" onClick={() => setTab('fees')} className="inline-flex min-h-6 items-center tap-area text-caption font-medium text-primary-600 hover:text-primary-700 hover:underline">
                Fee agreement
              </button>
            ),
          },
  ].filter(Boolean) as CheckItem[] : [];
  const rank = { blocked: 0, warning: 1, todo: 2, done: 3 } as const;
  checks.sort((a, b) => rank[a.state] - rank[b.state]);

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Physician · certifying panel"
        title={p?.fullName ?? 'Loading…'}
        badges={p && signing && (
          <>
            <StatusBadge tone={p.status === 'ACTIVE' ? 'covered' : 'neutral'}>{p.status === 'ACTIVE' ? 'Active' : 'Suspended'}</StatusBadge>
            <StatusBadge tone={signing.canSign ? 'covered' : p.status === 'ACTIVE' ? 'danger' : 'neutral'}>
              {signing.canSign ? 'Can sign' : 'Cannot sign'}
            </StatusBadge>
          </>
        )}
        subtitle={p && (
          <>
            {p.qualification ? `${p.qualification} · ` : ''}
            <span className="font-mono">{p.registrationNo}</span>
            {p.city ? ` · ${p.city}` : ''}
          </>
        )}
        actions={p && (can.edit || can.impersonate) && (
          <>
            {/* One click into their console, as them (owner, 29 Oct 2026). */}
            {can.impersonate && p.linked && p.loginStatus === 'ACTIVE' && (
              <Button variant="secondary" loading={signIn.busy !== null}
                onClick={() => void signIn.signInAs(`/admin/physicians/${p.id}/impersonate`)}>
                Sign in as
              </Button>
            )}
            {can.edit && <Button variant="secondary" onClick={() => setEditing(true)}>Edit details</Button>}
            {can.edit && (p.status === 'ACTIVE'
              ? <Button variant="secondary" onClick={() => setStatusIntent('SUSPENDED')}>Suspend</Button>
              : <Button onClick={() => setStatusIntent('ACTIVE')}>Reinstate</Button>)}
          </>
        )}
      />
      {signIn.error && <Alert tone="danger">{signIn.error}</Alert>}

      {!p && physician.loading && (
        <KeyFigures figures={['a', 'b', 'c'].map((k) => ({ key: k, label: ' ', value: '', loading: true }))} />
      )}

      {p && signing && (
        <>
          <KeyFigures
            className="sm:grid-cols-3 lg:grid-cols-3"
            figures={[
              {
                key: 'pending', label: 'Awaiting their review', value: p.pendingReviews ?? 0,
                hint: 'Examinations routed to them',
                tone: (p.pendingReviews ?? 0) > 0 && !signing.canSign ? 'overdue' : undefined,
              },
              { key: 'done', label: 'Certified', value: p.completedReviews ?? 0, hint: 'Examinations signed' },
              {
                key: 'avail', label: 'Availability', value: p.isAvailable !== false ? 'Available' : 'Away',
                hint: 'Set by the physician',
              },
            ]}
          />

          <AttentionPanel
            title={signing.canSign ? 'Can sign fitness certificates' : 'Cannot sign fitness certificates'}
            className="[&_ul]:grid [&_ul]:gap-x-6 [&_ul]:gap-y-2.5 [&_ul]:space-y-0 sm:[&_ul]:grid-cols-2 xl:[&_ul]:grid-cols-3"
            items={checks}
            summary={signing.canSign
              ? undefined
              : (p.pendingReviews ?? 0) > 0
                ? `${p.pendingReviews} examination${p.pendingReviews === 1 ? ' is' : 's are'} waiting on a signature that cannot be given. Fix the items above or reassign them.`
                : `Fix ${signing.reasons.length === 1 ? 'the item' : 'the items'} above before routing reviews here.`}
          />
        </>
      )}

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {p && tab === 'overview' && <Overview physician={p} />}

      {p && tab === 'fees' && <PayeeRates payeeType="PHYSICIAN" payeeId={p.id} onChanged={rates.reload} />}

      {p && tab === 'statements' && <PayeeSettlements payeeType="PHYSICIAN" payeeId={p.id} />}

      {p && tab === 'activity' && <Activity physicianId={p.id} />}

      {p && editing && (
        <PhysicianEditDialog
          physician={p}
          onClose={() => setEditing(false)}
          onDone={() => { setEditing(false); toast.toast('success', `${p.fullName} updated.`); physician.reload(); }}
        />
      )}
      {p && inviting && (
        <InviteDialog
          physician={p}
          onClose={() => setInviting(false)}
          onDone={(email) => {
            setInviting(false);
            toast.toast('success', `Invitation sent to ${email}.`);
            physician.reload();
          }}
        />
      )}
      {p && statusIntent && (
        <PhysicianStatusDialog
          physician={p}
          next={statusIntent}
          onClose={() => setStatusIntent(null)}
          onDone={() => { setStatusIntent(null); physician.reload(); }}
        />
      )}
    </div>
  );
}

function InlineAction({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex min-h-6 items-center tap-area text-caption font-medium text-primary-600 hover:text-primary-700 hover:underline">
      {children}
    </button>
  );
}

/**
 * Invites the physician to sign in: a panel login is created for the address
 * (or the waiting one is sent again) and the set-password email goes out.
 * Replaces linking an account by pasting its id (flow audit 2026-10-20, 12.2).
 */
function InviteDialog({ physician, onClose, onDone }: {
  physician: Physician;
  onClose: () => void;
  onDone: (email: string) => void;
}) {
  const resend = physician.linked && physician.loginStatus === 'INVITED';
  const [email, setEmail] = useState(physician.email ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = resend || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  async function submit() {
    setBusy(true); setError(null);
    try {
      const sent = await api.post<{ email: string }>(`/physicians/${physician.id}/invite`, resend ? {} : { email: email.trim() }, crypto.randomUUID());
      onDone(sent.email);
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={resend ? 'Resend the invitation' : `Invite ${physician.fullName} to sign in`} onClose={onClose}>
      {resend ? (
        <p className="text-small text-neutral-700">
          A new link to set a password is emailed to the address on their login. The earlier link stops working.
        </p>
      ) : (
        <TextField
          label="Email" value={email} onChange={setEmail} autoFocus required
          info="They receive a link to set a password. Their login reviews cases from every company, and only the cases assigned to them."
        />
      )}
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy} disabled={!ok}>{resend ? 'Resend invitation' : 'Send invitation'}</Button>
      </div>
    </Dialog>
  );
}

function Overview({ physician: p }: { physician: Physician }) {
  const none = <span className="text-neutral-500">Not recorded</span>;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <DetailSection title="Identity and qualification">
        <Facts
          columns={2}
          facts={[
            { label: 'Registration number', value: <span className="font-mono">{p.registrationNo}</span>, hint: 'Printed on every certificate they sign' },
            { label: 'Qualification', value: p.qualification ?? none },
            { label: 'AFIH number', value: p.afihNumber ? <span className="font-mono">{p.afihNumber}</span> : none },
            {
              label: 'Console login',
              value: p.linked === undefined ? none
                : !p.linked ? 'No account'
                : p.loginStatus === 'INVITED' ? 'Invited, not accepted yet'
                : p.loginCompanyBound ? 'Linked · one company only'
                : 'Linked',
            },
            {
              label: 'Serves', wide: true,
              value: (
                <span className="inline-flex items-center gap-1">
                  {/* Must agree with the login line above and the checklist: a
                      company-bound login only ever receives that company's cases. */}
                  {p.loginCompanyBound ? 'One company only — its login is bound to that company' : 'Every company and venue on the platform'}
                  <InfoTip label="About who they serve">
                    {p.loginCompanyBound
                      ? 'This login was created inside one company, so only that company\'s cases reach them. Support can move it to a panel login that serves every company.'
                      : `A physician belongs to the ${BRAND.name} panel; reviews are routed to them from any company.`}
                  </InfoTip>
                </span>
              ),
            },
            p.createdAt ? { label: 'On the panel since', value: formatDate(p.createdAt) } : null,
          ]}
        />
      </DetailSection>

      <DetailSection title="Contact">
        <Facts
          columns={2}
          facts={[
            { label: 'Mobile', value: p.mobile ? <a href={`tel:${p.mobile}`} className="font-mono text-primary-700 hover:underline">{p.mobile}</a> : none },
            { label: 'Email', value: p.email ? <a href={`mailto:${p.email}`} className="break-all text-primary-700 hover:underline">{p.email}</a> : none },
            { label: 'City', value: [p.city, p.state].filter(Boolean).join(', ') || none },
          ]}
        />
      </DetailSection>
    </div>
  );
}

function Activity({ physicianId }: { physicianId: string }) {
  const trail = useQuery<CursorPage<AuditRow>>(
    () => api.get(`/audit?entityType=Physician&entityId=${physicianId}&limit=50`),
    [physicianId],
  );

  return (
    <DetailSection title="Activity">
      <DataTable<AuditRow>
        rows={trail.data?.items ?? []} loading={trail.loading} error={trail.error} onRetry={trail.reload}
        rowKey={(r) => r.id}
        empty={{ title: 'Nothing recorded yet', hint: 'Joining the panel, suspensions and every change to this physician appear here.' }}
        columns={[
          { key: 'when', header: 'When', cell: (r) => <span className="text-xs text-neutral-600">{formatDateTime(r.occurredAt)}</span> },
          { key: 'what', header: 'What', cell: (r) => <span className="text-sm text-neutral-900">{actionWords(r.action)}</span> },
          {
            key: 'outcome', header: 'Outcome', mobileLabel: false,
            cell: (r) => (
              <StatusBadge tone={r.outcome === 'SUCCESS' ? 'covered' : 'overdue'}>
                {r.outcome === 'SUCCESS' ? 'Succeeded' : r.outcome === 'DENIED' ? 'Refused' : actionWords(r.outcome)}
              </StatusBadge>
            ),
          },
          {
            key: 'detail', header: 'Reason',
            cell: (r) => {
              const meta = (r.metadata ?? {}) as Record<string, unknown>;
              return <span className="text-xs text-neutral-600">{typeof meta.reason === 'string' ? meta.reason : '—'}</span>;
            },
          },
        ]}
      />
    </DetailSection>
  );
}

/** `physician.update` → "Physician update". */
function actionWords(key: string): string {
  const spaced = key.replace(/[._]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
